import { Banner, BlockStack, Button, Card, Text } from "@shopify/polaris";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { apiFetch } from "../api/client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import { t } from "../lib/i18n";

/**
 * 発注点の「なぜこの数字？」— 効く要因が7つあり、どれが当てはまるかは商品ごとに
 * 違うので、断片ではなく1段落にまとめて出す層。
 *
 * 🔴 **常時見えている数字と1行の内訳は決定論のまま**（この行の上に出ている）。
 * ここは押したときだけ出る層＝**監査できる根拠は LLM に依存しない**。
 * 🔑 **押されたときだけ呼ぶ**＝1画面50行・Pro なら商品1万件。常時生成は費用も
 * 待ち時間も成立しない。
 */

interface Props {
  shopifyVariantId: StockyPublicBigIntId;
}

interface ExplanationResponse {
  explanation: string;
  /** "ai"＝Gemini が書いた / "fallback"＝定型文（未設定・障害・検証で落ちた）。 */
  source: string;
}

export function ReorderPointExplanation({ shopifyVariantId }: Props) {
  const [opened, setOpened] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<ExplanationResponse>(
        `/api/reorder-policies/variants/${encodeURIComponent(canonicalStockyPublicBigIntId(shopifyVariantId))}/explanation`,
      ),
    onSuccess: () => setOpened(true),
  });

  if (!opened) {
    return (
      <BlockStack gap="150">
        <Button
          variant="plain"
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {t("なぜこの数字？")}
        </Button>
        {mutation.isError && (
          <Banner tone="critical" title={t("説明を取得できませんでした")}>
            <Text as="p" variant="bodySm">
              {t("時間をおいて、もう一度「なぜこの数字？」を押してください。")}
            </Text>
          </Banner>
        )}
      </BlockStack>
    );
  }

  return (
    <Card>
      <BlockStack gap="150">
        <Text as="p" variant="bodySm">
          {mutation.data?.explanation}
        </Text>
        {/* 🔴 出どころを隠さない＝AI が書いた文であることを明示する。
            数字は計算した値そのままだが、文章はモデルが組み立てている。 */}
        <Text as="p" variant="bodySm" tone="subdued">
          {mutation.data?.source === "ai"
            ? t("この説明はAIが作成しています。数値は計算結果をそのまま使っています。")
            : t("この説明は定型文です。")}
        </Text>
        <Button variant="plain" onClick={() => setOpened(false)}>
          {t("閉じる")}
        </Button>
      </BlockStack>
    </Card>
  );
}
